import { useQuery } from '@apollo/client';
import ClientSiteContext from '@nile/client_management/src/Contexts/ClientSiteContext';
import { GET_HOLIDAYS } from '@nile/banyan/src/Graphql/Mutations/holiday';
import {
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/banyan/src/Pebbles/select';
import {
  reactSelectErrorStyles,
  reactSelectInlineError
} from '@nile/banyan/src/Utils/style';
import {
  HeaderTag,
  getTranslatedText,
  AppContext,
  isAuthorised,
  WorkingHours,
  useUserMSPCheckHook,
  RMM_ONLY_PLANS,
  useIsAuthorisedHook,
  useCheckFeatureGating
} from '@nile/components';
import { AccountClient } from '@nile/graphql/src';
import { ErrorBoundary } from 'react-error-boundary';
import {
  RadioButton,
  Stack,
  TimezoneSelect,
  Dropdown
} from '@superops/ui-design-system';
import React, {
  Component,
  useCallback,
  useContext,
  useState,
  useEffect
} from 'react';
// import Select from 'react-select';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { BUSINESS_HOURS } from '@nile/client_management/src/Constants/Client';
import { cloneDeep } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { COMMON_CONSTANTS } from '@nile/components/src/Constants/Common';

const ErrorBoundaryComponent = () => <div>Error...</div>;

function BusinessHourForm(props) {
  const { site, errors, intl } = props;
  const { user, company } = useContext(AppContext);
  const reference = user?.role?.roleFeatureList || [];
  const flags = useFlags();

  const currentPlan = user?.chargeBeePlanId?.toLowerCase();
  const showHolidayList = useCheckFeatureGating('TICKETING')?.enabled;

  const [value, setValue] = useState(
    site.working24x7 ? 'working24x7' : 'optional'
  );
  const { loading, data, error } = useQuery(GET_HOLIDAYS, {
    client: AccountClient,
    skip: !isAuthorised(reference, 'holidayList.view')
  });

  const context = useContext(ClientSiteContext);
  const handleChange = useCallback((_checked, newValue) => {
    setValue(newValue);
    context.updateContext('working24x7', newValue === 'working24x7');
  }, []);

  const changeTimeZone = (zone) => {
    context.updateContext('timezoneCode', zone.value);
  };

  useEffect(() => {
    if (!site.timezoneCode && company) {
      const defaultZone = {
        value: company?.hqSite?.timezoneCode
      };
      changeTimeZone(defaultZone);
    }
  }, [company, site]);

  const changeHolidayList = (holiday) => {
    context.updateContext('holidayList', {
      id: holiday.id,
      name: holiday.name
    });
  };
  if (loading) return <div>loading...</div>;
  if (error) return <div>error...</div>;
  let holidayData = data?.getAllHolidayList.map((el) => {
    return {
      ...el,
      name: el.name,
      label: el.name,
      value: el.name
    };
  });

  const handleWorkingHourChange = (workinghour) => {
    const {
      hourType,
      start,
      end,
      day,
      checked,
      startHourType,
      endHourType
    } = workinghour;
    const updatedBusinessHours = cloneDeep(site.businessHour) || [];
    const index = updatedBusinessHours.findIndex((el) => el.day === day);
    if (checked && start && end) {
      if (index === -1) {
        updatedBusinessHours.push({
          day: day,
          start: start,
          end: end
        });
      } else {
        updatedBusinessHours[index] = {
          day: day,
          start: start,
          end: end
        };
      }
      context.updateContext('businessHour', updatedBusinessHours);
    }
    if (!checked) {
      if (index !== -1) {
        updatedBusinessHours.splice(index, 1);
        context.updateContext('businessHour', updatedBusinessHours);
      }
    }
  };

  const copyWorkingHours = (idx = 0) => {
    let updatedBusinessHours = cloneDeep(props.site?.businessHour);
    const dataToCopy = updatedBusinessHours[idx];
    updatedBusinessHours = BUSINESS_HOURS.map((el) => {
      return {
        ...el,
        start: dataToCopy.start,
        end: dataToCopy.end
      };
    });
    context.updateContext('businessHour', updatedBusinessHours);
  };

  return (
    <>
      <div>
        <div name="Business hour details" className="mb25">
          <p className="header-title mb-8">
            <FormattedMessage id="client.details.site.h2.businessheader" />
          </p>
          <div className="timezone">
            <p>
              <FormattedMessage id="client.details.site.timezone.header" />
            </p>
            <ErrorBoundary FallbackComponent={ErrorBoundaryComponent}>
              <TimezoneSelect
                value={{
                  value: site.timezoneCode
                }}
                onChange={changeTimeZone}
                components={{ IndicatorSeparator, DropdownIndicator }}
                classNamePrefix="value_selector"
                formatGroupLabel={formatGroupLabel}
                menuPlacement="auto"
                timezones={{ ...COMMON_CONSTANTS.TIMEZONES }}
                placeholder={getTranslatedText(
                  intl,
                  'client.new.business.timezone.label'
                )}
                menuPortalTarget={document.body}
                closeMenuOnScroll={(e) => {
                  return (
                    e.target &&
                    e.target.classList &&
                    !(e.target.classList.value.indexOf('menu-list') > -1)
                  );
                }}
                styles={
                  errors?.['site.timezoneCode']
                    ? {
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        ...reactSelectErrorStyles
                      }
                    : { menuPortal: (base) => ({ ...base, zIndex: 9999 }) }
                }
              />
            </ErrorBoundary>
            {errors?.['timezoneCode'] &&
              reactSelectInlineError(errors?.['timezoneCode']?.[0])}
          </div>
        </div>
        <div className="mb25">
          <Stack vertical>
            <RadioButton
              label={getTranslatedText(
                intl,
                'client.details.site.h2.businessheader2'
              )}
              checked={value === 'working24x7'}
              id="working24x7"
              name="accounts"
              onChange={handleChange}
            />
            <RadioButton
              label={getTranslatedText(intl, 'client.details.site.h2.custom')}
              id="optional"
              name="accounts"
              checked={value === 'optional'}
              onChange={handleChange}
            />
          </Stack>
          {value !== 'working24x7' && (
            <WorkingHours
              intl={intl}
              site={props.site}
              handleChange={handleWorkingHourChange}
              copyWorkingHours={copyWorkingHours}
              businessHours={
                props.site?.businessHour
                  ? props.site?.businessHour
                  : BUSINESS_HOURS
              }
            />
          )}
          {isAuthorised(reference, 'holidayList.view') && showHolidayList && (
            <div className="mt25">
              <div className="mt-8 mr-10">
                <p className="mb-8">
                  <FormattedMessage id="client.details.site.holidayList.header" />
                </p>
              </div>
              <Dropdown
                value={holidayData?.find(
                  (el) => el.name === site?.holidayList?.name
                )}
                label={getTranslatedText(intl, 'client.details.site.timezone2')}
                options={holidayData}
                onChange={changeHolidayList}
                components={{ IndicatorSeparator, DropdownIndicator }}
                classNamePrefix="holiday_value_selector"
                formatGroupLabel={formatGroupLabel}
                menuPlacement="auto"
                placeholder={getTranslatedText(
                  intl,
                  'client.details.site.chooseHoliday'
                )}
                menuPortalTarget={document.body}
                closeMenuOnScroll={(e) => {
                  return (
                    e.target &&
                    e.target.classList &&
                    !(e.target.classList.value.indexOf('menu-list') > -1)
                  );
                }}
                styles={
                  errors?.holidayList
                    ? {
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                        ...reactSelectErrorStyles
                      }
                    : {
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }
                }
              />
              {errors?.holidayList &&
                reactSelectInlineError(errors.holidayList[0])}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default injectIntl(BusinessHourForm);
